import { useCallback } from "react"
import type { PartnerContact } from "@stockline/shared/types/crm/partner-contact"
import { PARTNER_CONTACT_TABLE } from "../../../lib/offline/resources/crm/partner-contact/db"
import { useAISync } from "../../ai/providers/AISyncProvider"

export interface CreatePartnerContactInput {
  partnerId: string
  name: string
  surname: string
  companyOwner?: boolean
  phones?: string[] | null
  emails?: string[] | null
  role?: string | null
  aiContext?: string | null
}

export interface UpdatePartnerContactInput {
  name?: string
  surname?: string
  companyOwner?: boolean
  phones?: string[] | null
  emails?: string[] | null
  role?: string | null
  aiContext?: string | null
}

export function usePartnerContactMutations() {
  const { storage, changeQueue, engine } = useAISync()

  const createPartnerContact = useCallback(
    async (input: CreatePartnerContactInput): Promise<PartnerContact> => {
      const id = crypto.randomUUID()
      const now = new Date().toISOString()

      const contact: PartnerContact = {
        id,
        companyId: "",
        partnerId: input.partnerId,
        companyOwner: input.companyOwner ?? false,
        name: input.name,
        surname: input.surname,
        phones: input.phones ?? null,
        emails: input.emails ?? null,
        role: input.role ?? null,
        aiContext: input.aiContext ?? null,
        deletedAt: null,
        syncId: 0,
        createdAt: now,
        updatedAt: now,
      }

      await storage.put(PARTNER_CONTACT_TABLE, contact)
      await changeQueue.enqueue("partner-contacts", {
        op: "create",
        id,
        data: contact,
      })
      void engine.pushChanges("partner-contacts")

      return contact
    },
    [storage, changeQueue, engine]
  )

  const updatePartnerContact = useCallback(
    async (id: string, input: UpdatePartnerContactInput): Promise<void> => {
      const existing = await storage.get<PartnerContact>(
        PARTNER_CONTACT_TABLE,
        id
      )
      if (!existing) return

      const updated: PartnerContact = {
        ...existing,
        ...input,
        updatedAt: new Date().toISOString(),
      }

      await storage.put(PARTNER_CONTACT_TABLE, updated)
      await changeQueue.enqueue("partner-contacts", {
        op: "update",
        id,
        data: input,
      })
      void engine.pushChanges("partner-contacts")
    },
    [storage, changeQueue, engine]
  )

  const deletePartnerContact = useCallback(
    async (id: string): Promise<void> => {
      await storage.delete(PARTNER_CONTACT_TABLE, id)
      await changeQueue.enqueue("partner-contacts", {
        op: "delete",
        id,
      })
      void engine.pushChanges("partner-contacts")
    },
    [storage, changeQueue, engine]
  )

  return { createPartnerContact, updatePartnerContact, deletePartnerContact }
}
